import { useRef } from 'react';
import { getApplicationInsights } from '@/lib/application-insights';
import { extractIceServerUrls } from '../utils';

export interface UseVoiceLiveTelemetryOptions {
  /** Scenario id, merged into every tracked event/exception. */
  scenarioId: string;
}

export interface UseVoiceLiveTelemetryResult {
  /** Record the backend host (incl. port) for the active WebSocket. */
  setBackendHost: (host: string) => void;
  /** Current backend host, or '' before a connection attempt. */
  backendHost: () => string;
  /** Snapshot `performance.now()` as the start of a connect attempt. */
  markConnectStart: () => void;
  /** Milliseconds since the last {@link markConnectStart}, rounded. */
  elapsedMs: () => number;
  /** Extract + store STUN/TURN URLs (credentials excluded) from ICE servers. */
  setIceServerUrls: (servers: RTCIceServer[]) => void;
  /** Current ICE server URLs string, or '' before any are received. */
  iceServerUrls: () => string;
  /** Track an event; `backendHost` + `scenarioId` are merged automatically. */
  track: (name: string, properties?: Record<string, unknown>) => void;
  /** Track an exception; `backendHost` + `scenarioId` are merged automatically. */
  trackException: (error: unknown, properties?: Record<string, unknown>) => void;
}

/**
 * Owns the App Insights instance and the stable telemetry context for a Voice
 * Live session (backend host, connect-start time, ICE server URLs). Exposes
 * `track`/`trackException` that auto-merge `{ backendHost, scenarioId }` so call
 * sites only pass the per-event fields (e.g. `userId`, timings). The context
 * accessors return refs' current values, so they are safe to read from async
 * callbacks without stale-closure risk.
 */
export const useVoiceLiveTelemetry = ({
  scenarioId,
}: UseVoiceLiveTelemetryOptions): UseVoiceLiveTelemetryResult => {
  const appInsightsRef = useRef(getApplicationInsights());
  const backendHostRef = useRef<string>(''); // url.host of the WebSocket backend (includes port)
  const connectStartTimeRef = useRef<number>(0); // performance.now() at the start of each connect attempt
  const iceServerUrlsRef = useRef<string>(''); // extracted STUN/TURN URLs (credentials excluded)

  const setBackendHost = (host: string) => {
    backendHostRef.current = host;
  };
  const backendHost = () => backendHostRef.current;

  const markConnectStart = () => {
    connectStartTimeRef.current = performance.now();
  };
  const elapsedMs = () => Math.round(performance.now() - connectStartTimeRef.current);

  const setIceServerUrls = (servers: RTCIceServer[]) => {
    iceServerUrlsRef.current = extractIceServerUrls(servers);
  };
  const iceServerUrls = () => iceServerUrlsRef.current;

  const track = (name: string, properties?: Record<string, unknown>) => {
    appInsightsRef.current?.trackEvent({
      name,
      properties: { backendHost: backendHostRef.current, scenarioId, ...properties },
    });
  };

  const trackException = (error: unknown, properties?: Record<string, unknown>) => {
    appInsightsRef.current?.trackException({
      exception: error instanceof Error ? error : new Error(String(error)),
      properties: { backendHost: backendHostRef.current, scenarioId, ...properties },
    });
  };

  return {
    setBackendHost,
    backendHost,
    markConnectStart,
    elapsedMs,
    setIceServerUrls,
    iceServerUrls,
    track,
    trackException,
  };
};
